import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Gauge,
  LogOut,
  MoreVertical,
  Phone,
  Plus,
  Search,
  Settings,
  Users,
  Video,
} from 'lucide-react';
import type { ChatSummary, DeleteScope, MessageDto, ReplyTo } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { useCall } from '../calls/CallContext.tsx';
import { useChat, type MessageView } from '../chat/ChatContext.tsx';
import { Avatar } from '../components/Avatar.tsx';
import {
  InstallBanner,
  NotificationsBanner,
  OfflineBar,
  UpdateBanner,
} from '../components/Banners.tsx';
import { ChatList } from '../components/ChatList.tsx';
import { Composer, type SlashCommand } from '../components/Composer.tsx';
import { ImageViewer, type ViewerImage } from '../components/ImageViewer.tsx';
import { ChatActions } from '../components/ChatActions.tsx';
import { MessageActions } from '../components/MessageActions.tsx';
import {
  ChatMatches,
  SearchField,
  SearchNavBar,
  SearchResults,
} from '../components/MessageSearch.tsx';
import { ConnectionLost } from '../components/ConnectionLost.tsx';
import { Loading } from '../components/Loading.tsx';
import { MessageThread } from '../components/MessageThread.tsx';
import { NewChatDialog } from '../components/NewChatDialog.tsx';
import { TypingDots } from '../components/TypingDots.tsx';
import { useAiPrefs } from '../lib/aiPrefs.ts';
import { formatMembers, presenceLabel } from '../lib/format.ts';
import { messagePreview } from '../lib/reply.ts';
import { selectedText, wholeSpan, type Span } from '../lib/selection.ts';
import { themeBackground, useTheme } from '../lib/theme.ts';
import { useMessageSearch } from '../lib/useMessageSearch.ts';
import { useMinuteTick } from '../lib/useMinuteTick.ts';
import { useNotifications } from '../lib/useNotifications.ts';
import { useAndroidUpdate } from '../lib/useAndroidUpdate.ts';
import { useOnline } from '../lib/useOnline.ts';
import { usePwaInstall } from '../lib/usePwaInstall.ts';
import './ChatScreen.css';

/**
 * What the assistant's chat offers behind a leading «/». Mirrors commandReply
 * in server/src/ai.ts — the server answers these; this list only shows them.
 */
const AI_COMMANDS: SlashCommand[] = [
  { command: '/новый чат', description: 'забыть прошлый разговор' },
  { command: '/баланс', description: 'остаток дневного лимита' },
  { command: '/помощь', description: 'что умеет ассистент и список команд' },
];

export function ChatScreen() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const {
    chats,
    chatsLoading,
    chatsFailed,
    retryChats,
    setActiveChatId,
    messages,
    messagesLoading,
    messagesReady,
    messagesFailed,
    retryMessages,
    hasMoreMessages,
    loadingOlderMessages,
    loadOlderMessages,
    hasNewerMessages,
    loadNewerMessages,
    focusMessage,
    endThreadWindow,
    sendText,
    sendFile,
    sendMedia,
    sendAlbum,
    retrySend,
    markViewed,
    transcribe,
    editText,
    deleteMessage,
    setReaction,
    pinChat,
    startChat,
    typingChatIds,
    reportTyping,
  } = useChat();
  const { placeCall } = useCall();
  const online = useOnline();
  const theme = useTheme();
  const { canInstall, install, dismiss } = usePwaInstall();
  const notifications = useNotifications();
  // The chat id doubles as the «actively using this» signal: opening a
  // conversation re-asks about a newer APK, throttled inside the hook.
  const update = useAndroidUpdate(chatId);

  /**
   * The field above the chat list, which asks two questions with one word: the
   * names it matches are filtered here, and the messages it matches come from
   * the server. Its text lives in the search rather than in a state of its own,
   * so there is one answer to «что набрано» instead of two that can drift.
   */
  const listSearch = useMessageSearch('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  /** The ⋮ popover over the chat list. */
  const [menuOpen, setMenuOpen] = useState(false);
  /** The ⋮ popover in the conversation header, which is a different one. */
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  /** The message under the long-press menu, and the one being rewritten. */
  const [held, setHeld] = useState<MessageView | null>(null);
  /** The chat row under the same gesture, over in the list. */
  const [heldChat, setHeldChat] = useState<ChatSummary | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  /**
   * The message being answered — from a swipe left, from the menu, or from
   * «Цитировать» over a selected fragment, which is the one that also carries
   * the words rather than only the id.
   */
  const [answering, setAnswering] = useState<ReplyTo | null>(null);
  /** The fragment somebody is holding handles around, and which message it is in. */
  const [picked, setPicked] = useState<{ id: string; span: Span } | null>(null);
  /**
   * Which match is open in the thread, as its place in the list — null while
   * the list itself is what is on screen. The place rather than the id because
   * the strip counts with it («2 из 4») and the arrows step through it.
   */
  const [openMatch, setOpenMatch] = useState<number | null>(null);
  /** What the thread is being told to jump to; the stamp re-arms the same one. */
  const [focus, setFocus] = useState<{ id: string; at: number } | null>(null);

  /**
   * The cleanup is the load-bearing half.
   *
   * `/settings`, `/new-group` and the two `/chat/:id/…` screens are siblings of
   * this route rather than children, so leaving for one of them unmounts this
   * screen while the provider — and everything reading `activeChatId` — stays
   * up. Without the reset the app went on claiming to be reading a conversation
   * nobody had on screen: the server suppressed its notifications, and the read
   * receipt in ChatContext kept firing at every new message in it, so anything
   * arriving while somebody was in the settings was marked read, badged nothing
   * and notified nowhere.
   *
   * Moving between two conversations is unaffected — this screen stays mounted,
   * so the null and the new id are set in one batch and no render sees the gap.
   */
  useEffect(() => {
    setActiveChatId(chatId ?? null);
    return () => setActiveChatId(null);
  }, [chatId, setActiveChatId]);

  // The scrim closes the popover on a tap; Escape is the same gesture with a
  // keyboard, and the rest of the app already answers to it. Both menus, since
  // only one of them can be open and neither wants its own listener.
  useEffect(() => {
    if (!menuOpen && !chatMenuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      setChatMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, chatMenuOpen]);

  // Leaving the conversation abandons all of them: a menu belongs to a message
  // that is no longer on screen, and an edit, a reply or a held fragment belongs
  // to a thread nobody is looking at.
  useEffect(() => {
    setHeld(null);
    setEditingId(null);
    setAnswering(null);
    setPicked(null);
    setChatMenuOpen(false);
  }, [chatId]);

  // The message being edited can be deleted from the other device mid-sentence.
  const editing = messages.find((message) => message.id === editingId) ?? null;
  useEffect(() => {
    if (editingId !== null && editing === null) setEditingId(null);
  }, [editingId, editing]);

  // …and so can the one being answered, which is the same problem: the banner
  // would go on quoting something the thread no longer holds.
  const replyTo = messages.find((message) => message.id === answering?.id) ?? null;
  useEffect(() => {
    if (answering !== null && replyTo === null) setAnswering(null);
  }, [answering, replyTo]);

  // A fragment is held in one message, and it goes with it.
  const pickedMessage = messages.find((message) => message.id === picked?.id) ?? null;
  useEffect(() => {
    if (picked !== null && pickedMessage === null) setPicked(null);
  }, [picked, pickedMessage]);

  /** Answering and rewriting are two different messages; only one at a time. */
  function startReply(message: MessageView, quote: string | null = null) {
    setEditingId(null);
    setAnswering({ id: message.id, quote });
  }

  /** The words inside the handles, in body form — codes and all. */
  const pickedText = () =>
    picked && pickedMessage?.body ? selectedText(pickedMessage.body, picked.span) : '';

  const activeChat = chats.find((chat) => chat.id === chatId) ?? null;
  /**
   * Whether there is somebody on the other end to ring: a chat of two, and not
   * a bot's. Read twice — the video button and the menu's «Звонок» are the same
   * question asked about the same peer.
   */
  const canCall =
    activeChat?.kind === 'direct' &&
    activeChat.peer !== null &&
    !activeChat.peer.assistantProvider;
  // Only «был(а) в сети N минут назад» goes stale on its own; the clock and the
  // date do not, and neither does «в сети».
  const now = useMinuteTick(
    activeChat !== null && !activeChat.online && activeChat.lastSeenAt !== null,
  );
  const historyState = location.state as {
    image?: ViewerImage;
    search?: boolean;
    /** Carried in from the chat list: the word, and the match to stand on. */
    query?: string;
    focusId?: string;
  } | null;
  const viewerImage = historyState?.image ?? null;
  /**
   * The open search is a history entry for the same reason the viewer is: on
   * Android the hardware back button has to leave the search rather than the
   * app. What is typed into it stays component state — a query does not belong
   * in the history stack, and the effect below clears it when the entry goes.
   */
  const searchOpen = historyState?.search === true;
  const messageSearch = useMessageSearch(
    searchOpen ? (chatId ?? null) : null,
    historyState?.query ?? '',
  );
  // Fetched only where it is spent. A chat with no bot in it never asks.
  const { prefs: aiPrefs } = useAiPrefs(activeChat?.peer?.assistantProvider != null);
  const query = listSearch.query.trim().toLowerCase();
  // The title is the nickname on a direct chat and the group's name on a group,
  // which is exactly what the row shows — so search matches what is on screen.
  const visibleChats = query
    ? chats.filter((chat) => chat.title.toLowerCase().includes(query))
    : chats;

  function openChat(chat: ChatSummary) {
    navigate(`/chat/${chat.id}`);
  }

  /**
   * A message found from the chat list opens its conversation with the search
   * already open on the same word and standing on that message. The word and
   * the id travel in the history entry rather than in a store, because that is
   * what the conversation's own search is already made of — and it means the
   * back button lands on the list with the field as it was left.
   */
  function openFoundMessage(message: MessageDto) {
    navigate(`/chat/${message.conversationId}`, {
      state: { search: true, query: listSearch.query, focusId: message.id },
    });
  }

  /**
   * The open viewer is a history entry rather than component state, so the
   * Android back button dismisses it instead of leaving the app: back pops the
   * entry, the state goes away, and the overlay unmounts. Closing from the UI
   * goes through the same entry (`navigate(-1)`) to keep the stack balanced.
   *
   * The state it pushes carries whatever was already there, so a picture opened
   * from a thread that is showing a search match comes back to that match
   * rather than to a closed search.
   */
  function openImage(image: ViewerImage) {
    navigate(location.pathname + location.search, { state: { ...historyState, image } });
  }

  function openSearch() {
    navigate(location.pathname + location.search, { state: { ...historyState, search: true } });
  }

  /**
   * «‹» in the search header, and the hardware back button through the same
   * entry. From an open match it goes back to the list of them; from the list
   * it leaves the search — which is the one place the thread has to be put
   * back on its newest page, since a match may have opened a window into the
   * middle of the conversation.
   */
  const leaveSearch = useCallback(() => {
    if (openMatch !== null) {
      setOpenMatch(null);
      setFocus(null);
      return;
    }
    navigate(-1);
  }, [openMatch, navigate]);

  const { reset: resetSearch } = messageSearch;
  useEffect(() => {
    if (searchOpen) return;
    setOpenMatch(null);
    setFocus(null);
    resetSearch();
    endThreadWindow();
  }, [searchOpen, resetSearch, endThreadWindow]);

  /**
   * Opens one match in the thread: the window arrives first if the message is
   * further back than the pages on screen, and only then is the thread told to
   * go to it — a jump asked for before its message exists would find nothing.
   */
  const { results: matches } = messageSearch;
  const openMatchAt = useCallback(
    async (index: number) => {
      const message = matches[index];
      if (!message) return;
      setOpenMatch(index);
      await focusMessage(message.id);
      setFocus({ id: message.id, at: Date.now() });
    },
    [matches, focusMessage],
  );

  /**
   * A match tapped in the chat list arrives here as an id in the history entry,
   * a moment before the conversation's own search has finished asking the same
   * question. It opens as soon as that answer contains it — and only once, so
   * stepping away with the arrows is not undone by the next page of matches.
   */
  const arrivedOn = historyState?.focusId;
  const arrived = useRef<string | null>(null);
  useEffect(() => {
    if (arrivedOn === undefined || arrived.current === arrivedOn) return;
    const index = matches.findIndex((message) => message.id === arrivedOn);
    if (index < 0) return;
    // Once per arrival: without this, «‹» back to the list of matches would be
    // answered by this effect opening the same message again.
    arrived.current = arrivedOn;
    void openMatchAt(index);
  }, [arrivedOn, matches, openMatchAt]);

  /**
   * The arrows in the strip. Up is older and down is newer, which is the thread
   * they are stepping through rather than the list, where the order is the
   * other way round. The page after this one is asked for a few matches early,
   * so the arrow is almost never the thing waiting for it.
   */
  function stepMatch(delta: 1 | -1) {
    if (openMatch === null) return;
    const next = openMatch + delta;
    if (next < 0 || next >= messageSearch.results.length) return;
    if (messageSearch.hasMore && next >= messageSearch.results.length - 3) messageSearch.loadMore();
    void openMatchAt(next);
  }

  async function handleStart(nickname: string) {
    const chat = await startChat(nickname);
    setDialogOpen(false);
    openChat(chat);
  }

  return (
    <div className="app-frame" data-view={chatId ? 'conversation' : 'list'}>
      {/* Ahead of both of the others, and it can never collide with the first:
          an app offering to update itself is by definition already installed.
          It outranks the notification prompt because it is the shorter errand
          and because the newer build may be what fixes notifications. */}
      {update.release !== null && (
        <UpdateBanner
          versionName={update.release.versionName}
          stage={update.stage}
          progress={update.progress}
          message={update.message}
          onAct={update.act}
          onDismiss={update.dismiss}
        />
      )}

      {canInstall && <InstallBanner onInstall={install} onDismiss={dismiss} />}

      {/* Installing comes first: on iOS the notification CTA is meaningless
          until the app is on the home screen, so two banners never compete. */}
      {update.release === null && !canInstall && notifications.canPrompt && (
        <NotificationsBanner
          needsHomeScreen={notifications.needsHomeScreen}
          message={notifications.message}
          busy={notifications.busy}
          onEnable={() => void notifications.enable()}
          onDismiss={notifications.dismiss}
        />
      )}

      <div className="panes">
        <aside className="sidebar">
          <div className="sidebar-title-row">
            <h3 className="sidebar-title">Чаты</h3>
            {/* Everything the list offers lives behind the ⋮ now — starting a
                chat included, so the header carries one control instead of
                five competing for the same corner. */}
            <div className="sidebar-title-actions">
              <div className="popover-anchor">
                <button
                  type="button"
                  className="icon-button popover-button"
                  onClick={() => setMenuOpen((open) => !open)}
                  aria-label="Меню"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  <MoreVertical size={18} />
                </button>

                {menuOpen && (
                  <div className="popover" role="menu">
                    <button
                      type="button"
                      className="popover-item"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        setDialogOpen(true);
                      }}
                    >
                      <Plus size={16} />
                      Новый чат
                    </button>
                    <button
                      type="button"
                      className="popover-item"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/new-group');
                      }}
                    >
                      <Users size={16} />
                      Новая группа
                    </button>
                    <button
                      type="button"
                      className="popover-item"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/settings');
                      }}
                    >
                      <Settings size={16} />
                      Настройки
                    </button>
                    {user?.isAdmin && (
                      <button
                        type="button"
                        className="popover-item"
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/admin');
                        }}
                      >
                        <Gauge size={16} />
                        Панель администратора
                      </button>
                    )}
                    {/* Last, and set apart: it is the one item here that ends
                        the session rather than opening something. */}
                    <button
                      type="button"
                      className="popover-item popover-item-parted"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        void logout();
                      }}
                    >
                      <LogOut size={16} />
                      Выйти
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Catches the tap that lands anywhere else, which is what closes the
              popover — it is transparent, so nothing behind it dims. */}
          {menuOpen && (
            <div className="popover-scrim" onClick={() => setMenuOpen(false)} />
          )}

          {!online && <OfflineBar />}

          <div className="sidebar-controls">
            <input
              className="text-field sidebar-search"
              value={listSearch.query}
              onChange={(event) => listSearch.setQuery(event.target.value)}
              placeholder="Поиск"
              aria-label="Поиск"
            />
          </div>

          {chats.length === 0 && chatsFailed ? (
            <ConnectionLost
              text="Чаты не загрузились"
              busy={chatsLoading}
              onRetry={retryChats}
            />
          ) : (
          <ChatList
            chats={visibleChats}
            activeChatId={chatId ?? null}
            loading={chatsLoading || listSearch.loading}
            filtered={query.length > 0}
            typingChatIds={typingChatIds}
            onSelect={openChat}
            onHold={setHeldChat}
            after={
              listSearch.results.length === 0 ? null : (
              <ChatMatches
                query={listSearch.query}
                results={listSearch.results}
                total={listSearch.total}
                hasMore={listSearch.hasMore}
                onLoadMore={listSearch.loadMore}
                chats={chats}
                onOpen={openFoundMessage}
              />
              )
            }
          />
          )}
        </aside>

        <section className="conversation">
          {activeChat ? (
            <>
              {/* The three screens of design/design_handoff_search are three
                  states of this one: the field replaces the header, the list
                  of matches replaces the thread, and the strip replaces the
                  composer once one of them is open. */}
              {searchOpen ? (
                <SearchField
                  query={messageSearch.query}
                  onQuery={messageSearch.setQuery}
                  onBack={leaveSearch}
                />
              ) : (
              <header className="conversation-header">
                <button
                  type="button"
                  className="icon-button conversation-back"
                  onClick={() => navigate('/')}
                >
                  <ChevronLeft size={18} />
                  <span>Назад</span>
                </button>

                {/* In a group the identity is the way in to «Информация о
                    группе» — the roster has to hang off something, and the
                    name of the thing it describes is that something. A direct
                    chat has nothing behind it, so it stays inert. */}
                {activeChat.kind === 'group' ? (
                  <button
                    type="button"
                    className="conversation-identity-link"
                    onClick={() => navigate(`/chat/${activeChat.id}/info`)}
                  >
                    <Avatar
                      className="conversation-avatar conversation-avatar-group"
                      photoId={activeChat.avatarPhotoId}
                    >
                      <Users size={19} />
                    </Avatar>
                    <div className="conversation-identity">
                      <div className="conversation-name">{activeChat.title}</div>
                      {typingChatIds.includes(activeChat.id) ? (
                        <div className="conversation-status conversation-typing">
                          печатает
                          <TypingDots />
                        </div>
                      ) : (
                        <div className="conversation-status">
                          {formatMembers(activeChat.memberCount ?? 0)}
                        </div>
                      )}
                    </div>
                  </button>
                ) : activeChat.kind === 'notes' ? (
                  /* The same way in as a person's, and for the same reason: the
                     name is what the thing behind it is named after. What is
                     behind it differs — a person's profile is read, and this one
                     is written, because the name and the picture on «Заметки»
                     are their owner's to choose.

                     No status line to swap under the name: presence is a fact
                     about somebody else, and there is nobody else. So «открыть
                     профиль» is the whole of the second line rather than the
                     press-state of one, and the chevron carries the rest. */
                  <button
                    type="button"
                    className="conversation-identity-link conversation-identity-peer"
                    onClick={() => navigate(`/chat/${activeChat.id}/info`)}
                  >
                    <Avatar
                      className="conversation-avatar conversation-avatar-group"
                      photoId={activeChat.avatarPhotoId}
                    >
                      <Bookmark size={19} />
                    </Avatar>

                    <div className="conversation-identity">
                      <div className="conversation-name">{activeChat.title}</div>
                      <div className="conversation-status">открыть профиль</div>
                    </div>

                    <ChevronRight className="conversation-chevron" size={15} />
                  </button>
                ) : activeChat.peer?.assistantProvider ? (
                  <>
                    {/* A bot's face is a button into its settings, the way a
                        group's is a button into the roster and a person's is a
                        button into their profile. Each face leads to the thing
                        behind that kind of correspondent; a bot has no profile
                        and no shared photographs, so its own settings are it. */}
                    <button
                      type="button"
                      className="conversation-avatar-button"
                      onClick={() => navigate(`/chat/${activeChat.id}/bot`)}
                      aria-label="Настройки бота"
                    >
                      <Avatar
                        className="conversation-avatar"
                        photoId={activeChat.peer.avatarPhotoId}
                        name={activeChat.title}
                      />
                    </button>

                    <div className="conversation-identity">
                      <div className="conversation-name">{activeChat.title}</div>
                      {/* «печатает…» takes over the status line while it lasts,
                          the way it does in Telegram — the presence it replaces
                          is implied by it. */}
                      {typingChatIds.includes(activeChat.id) ? (
                        <div className="conversation-status conversation-typing">
                          печатает
                          <TypingDots />
                        </div>
                      ) : (
                        /* Presence means «has an open socket», and a bot has
                           none — so it read «не в сети» forever, about the one
                           correspondent that is always there. What is worth
                           saying instead is which one it is: the two answer on
                           different APIs and the composer offers different
                           things in each. */
                        <div className="conversation-status">
                          бот · {activeChat.peer.assistantProvider === 'openai' ? 'OpenAI' : 'Gemini'}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* The way into the other person's profile and the media you
                     have sent each other.

                     Three things carry the fact that it is pressable, because
                     one is not enough on a row that spent this app's whole life
                     being inert: the chevron, the row lighting up on press, and
                     the status line turning into the action it performs. The
                     last of those is a pair of lines swapped by :active rather
                     than state — the press is the browser's to know about, and
                     re-rendering the header on touchdown to change one word
                     would be paying a render for a hover effect. */
                  <button
                    type="button"
                    className="conversation-identity-link conversation-identity-peer"
                    onClick={() => navigate(`/chat/${activeChat.id}/info`)}
                  >
                    <Avatar
                      className="conversation-avatar"
                      photoId={activeChat.peer?.avatarPhotoId}
                      name={activeChat.title}
                    />

                    <div className="conversation-identity">
                      <div className="conversation-name">{activeChat.title}</div>
                      {typingChatIds.includes(activeChat.id) ? (
                        <div className="conversation-status conversation-typing">
                          печатает
                          <TypingDots />
                        </div>
                      ) : (
                        <>
                          <div className="conversation-status conversation-status-idle">
                            <span
                              className={`presence-dot${activeChat.online ? ' presence-dot-online' : ''}`}
                            />
                            {presenceLabel(
                              activeChat.online,
                              activeChat.lastSeenAt,
                              activeChat.peer?.gender ?? null,
                              now,
                            )}
                          </div>
                          <div className="conversation-status conversation-status-press">
                            открыть профиль
                          </div>
                        </>
                      )}
                    </div>

                    <ChevronRight className="conversation-chevron" size={15} />
                  </button>
                )}

                {/* Two controls at the right edge and no more, because the
                    third one was costing the name beside it: a header with an
                    avatar, «был(а) вчера в 22:15» and three buttons has to cut
                    something on a phone, and it cut the words. The video call
                    stays out because it is the one thing here nothing else
                    stands in for; the audio call and the search moved behind
                    the ⋮, where the list's own menu has been living.

                    Calls are 1:1 only — a group has no single other person to
                    ring, and the server refuses one anyway. So a group's menu
                    is «Поиск в чате» alone, and the video button is not drawn.

                    Deliberately NOT disabled when the peer shows as «не в сети»,
                    which is where this departs from §1 of the calls handoff.
                    Presence here means "has an open socket" and says nothing
                    about whether a push can still wake them, which is the
                    ordinary way one person reaches another — a greyed-out button
                    would be refusing the most common case. An unreachable peer
                    rings out and lands in the thread as «Пропущенный», exactly
                    as a telephone behaves.

                    Not in a bot's chat. There is nothing on the other end to
                    pick up — the account has no session, no device and no
                    token — so the ring would time out into «Пропущенный» every
                    time, which is a button whose only outcome is a wrong
                    answer. */}
                <div className="conversation-actions">
                  {canCall && (
                    <button
                      type="button"
                      className="icon-button conversation-action"
                      onClick={() =>
                        placeCall(
                          activeChat.id,
                          activeChat.peer!.id,
                          activeChat.title,
                          activeChat.peer!.avatarPhotoId,
                          true,
                        )
                      }
                      aria-label="Видеозвонок"
                    >
                      <Video size={18} />
                    </button>
                  )}

                  <div className="popover-anchor">
                    <button
                      type="button"
                      className="icon-button conversation-action"
                      onClick={() => setChatMenuOpen((open) => !open)}
                      aria-label="Ещё"
                      aria-haspopup="menu"
                      aria-expanded={chatMenuOpen}
                    >
                      <MoreVertical size={18} />
                    </button>

                    {chatMenuOpen && (
                      <div className="popover" role="menu">
                        {canCall && (
                          <button
                            type="button"
                            className="popover-item"
                            role="menuitem"
                            onClick={() => {
                              setChatMenuOpen(false);
                              placeCall(
                                activeChat.id,
                                activeChat.peer!.id,
                                activeChat.title,
                                activeChat.peer!.avatarPhotoId,
                                false,
                              );
                            }}
                          >
                            <Phone size={16} />
                            Звонок
                          </button>
                        )}
                        <button
                          type="button"
                          className="popover-item"
                          role="menuitem"
                          onClick={() => {
                            setChatMenuOpen(false);
                            openSearch();
                          }}
                        >
                          <Search size={16} />
                          Поиск в чате
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </header>
              )}

              {/* The tap that lands anywhere else in the conversation, which is
                  what closes the menu. Transparent, and a sibling of the header
                  rather than a child of it: it has to cover the thread. */}
              {chatMenuOpen && (
                <div className="popover-scrim" onClick={() => setChatMenuOpen(false)} />
              )}

              {searchOpen && openMatch === null ? (
                <SearchResults
                  query={messageSearch.query}
                  results={messageSearch.results}
                  total={messageSearch.total}
                  loading={messageSearch.loading}
                  hasMore={messageSearch.hasMore}
                  onLoadMore={messageSearch.loadMore}
                  onOpen={(message) =>
                    void openMatchAt(
                      messageSearch.results.findIndex((item) => item.id === message.id),
                    )
                  }
                />
              ) : (
              <>
              {/* The wallpaper is painted once here, behind the thread *and*
                  the composer: the composer is floating elements over the
                  conversation now, and a background painted per-child would
                  break its gradient at the seam between them. The wallpaper is
                  part of the theme, which is a global preference, so every
                  conversation reads the same one. */}
              <div
                className="conversation-body"
                style={{ background: themeBackground(theme) }}
              >
              {messagesFailed && messages.length === 0 ? (
                <ConnectionLost
                  text="Сообщения не загрузились"
                  busy={messagesLoading}
                  onRetry={retryMessages}
                />
              ) : messages.length === 0 && !messagesReady ? (
                /* The wait, said out loud. Not `messagesLoading`: that flag is
                   about a request in flight, and between opening the chat and
                   the fetch starting there are frames where it is false and the
                   thread is just as empty — a blink of nothing before the
                   spinner is the same silence in miniature. What this asks
                   instead is whether a page has ever landed for this chat, so
                   the answer holds across the whole wait and stops the moment
                   there is something to draw. A chat that really is empty has
                   landed and gets the thread, which draws its own nothing —
                   the right nothing, this time. */
                <Loading text="Загружаем сообщения…" />
              ) : (
              <MessageThread
                messages={messages}
                /* Only a group needs names over its bubbles; in a chat of two
                   the side the bubble is on says everything. */
                showSenders={activeChat.kind === 'group'}
                /* «Заметки» have no reader but their writer, so a ✓ there would
                   be a receipt from nobody. */
                showTicks={activeChat.kind !== 'notes'}
                editingId={editingId}
                hasMore={hasMoreMessages}
                loadingOlder={loadingOlderMessages}
                hasNewer={hasNewerMessages}
                onLoadNewer={loadNewerMessages}
                focus={searchOpen ? focus : null}
                highlight={searchOpen ? messageSearch.query : undefined}
                onLoadOlder={loadOlderMessages}
                onOpenImage={openImage}
                onHold={setHeld}
                picked={picked}
                onPick={(message, span) => {
                  // The menu and the handles are two answers to the same hold,
                  // and the drag that started these is the one that dismisses it.
                  setHeld(null);
                  setPicked(span ? { id: message.id, span } : null);
                }}
                onPickCopy={() => {
                  // The fragment as it is stored, codes and all: pasted back into
                  // the composer it becomes the same smileys again, exactly as
                  // «Копировать» over a whole message does.
                  void navigator.clipboard?.writeText(pickedText());
                  setPicked(null);
                }}
                onPickQuote={() => {
                  if (pickedMessage) startReply(pickedMessage, pickedText());
                  setPicked(null);
                }}
                onReply={startReply}
                onViewed={(id) => void markViewed(id)}
                /* A deployment with no speech engine draws no «Аа» at all —
                   the session says whether there is one. */
                canTranscribe={user?.transcription.enabled === true}
                onTranscribe={(id) => void transcribe(id)}
                onRetry={retrySend}
                /* The same words, the same parent, the top tier — so it is the
                   request that was made again rather than a new one that
                   happens to be worded alike. `redo` is what keeps the re-sent
                   prompt from masquerading as something the person typed. */
                onRedraw={(image) =>
                  void sendText(
                    image.prompt,
                    image.sourceMessageId ? { id: image.sourceMessageId, quote: null } : null,
                    {
                      mode: 'image',
                      quality: 'high',
                      redo: true,
                    },
                  )
                }
              />
              )}

              {searchOpen && openMatch !== null ? (
                <SearchNavBar
                  position={openMatch + 1}
                  total={messageSearch.total}
                  createdAt={messageSearch.results[openMatch]?.createdAt ?? 0}
                  onOlder={openMatch + 1 < messageSearch.results.length ? () => stepMatch(1) : null}
                  onNewer={openMatch > 0 ? () => stepMatch(-1) : null}
                />
              ) : (
              <Composer
                /* The reply target is cleared by the send rather than by the
                   composer: an attachment and its caption go as two messages,
                   and both of them are answering the same thing. */
                onSendText={(body) => {
                  setAnswering(null);
                  void sendText(body, answering);
                }}
                onSendFile={(file) => {
                  setAnswering(null);
                  void sendFile(file, answering);
                }}
                onSendMedia={(media) => {
                  setAnswering(null);
                  void sendMedia(media, answering);
                }}
                onSendAlbum={(outgoing) => {
                  setAnswering(null);
                  void sendAlbum(outgoing, answering);
                }}
                /* So the compose screen can refuse a clip the server would,
                   before the whole group is uploaded behind it. */
                maxUploadBytes={user?.maxUploadBytes ?? Infinity}
                onTyping={reportTyping}
                editing={
                  editing?.kind === 'text' && editing.body !== null
                    ? { id: editing.id, body: editing.body }
                    : null
                }
                replying={
                  replyTo && {
                    id: replyTo.id,
                    name: replyTo.senderName,
                    // The fragment when there is one: it is the part being
                    // answered, and the banner is where that is confirmed before
                    // the reply goes.
                    preview: answering?.quote ?? messagePreview(replyTo),
                  }
                }
                onSaveEdit={(id, body) => {
                  setEditingId(null);
                  void editText(id, body);
                }}
                onCancelEdit={() => setEditingId(null)}
                onCancelReply={() => setAnswering(null)}
                commands={activeChat.peer?.assistant ? AI_COMMANDS : undefined}
                /* The shapes and the default tier are this reader's, not this
                   chat's — set once and they hold in both bots' chats. */
                assistant={
                  activeChat.peer?.assistantProvider
                    ? {
                        provider: activeChat.peer.assistantProvider,
                        ratios: aiPrefs.ratios,
                        quality: aiPrefs.quality,
                      }
                    : undefined
                }
              />
              )}
              </div>
              </>
              )}
            </>
          ) : chatsFailed && chatId ? (
            /* Not «Выберите чат»: there is no list to choose from, and this
               pane is the entire screen on a phone that was opened by tapping
               a notification — the one place the sentence was most wrong.
               Only with a chat in the URL, which is the same condition
               `data-view` uses to hide the sidebar: without one the list is on
               screen and already says this, and two copies of it side by side
               on a desktop is one too many. */
            <ConnectionLost text="Чаты не загрузились" busy={chatsLoading} onRetry={retryChats} />
          ) : (
            <div className="conversation-empty">
              <div className="conversation-empty-block" />
              <div className="conversation-empty-text">Выберите чат, чтобы начать общение</div>
            </div>
          )}
        </section>
      </div>

      {dialogOpen && (
        <NewChatDialog onClose={() => setDialogOpen(false)} onStart={handleStart} />
      )}

      {heldChat && (
        <ChatActions
          chat={heldChat}
          onClose={() => setHeldChat(null)}
          onPin={(pinned) => void pinChat(heldChat.id, pinned)}
        />
      )}

      {held && (
        <MessageActions
          message={held}
          assistant={activeChat?.peer?.assistantProvider != null}
          onClose={() => setHeld(null)}
          onEdit={() => {
            setAnswering(null);
            setEditingId(held.id);
            setHeld(null);
          }}
          onReply={() => {
            startReply(held);
            setHeld(null);
          }}
          onForward={() => {
            // The message id in the path rather than in history state: the
            // picker is a screen of its own, and one reloaded on a phone should
            // still know what it is forwarding.
            setHeld(null);
            navigate(`/chat/${held.conversationId}/forward/${held.id}`);
          }}
          onSelect={() => {
            // The whole message to begin with: both handles are then on screen
            // and either one narrows it, which is a shorter way to any fragment
            // than starting from a word somebody has to find first.
            setPicked({ id: held.id, span: wholeSpan(held.body ?? '') });
            setHeld(null);
          }}
          onDelete={(scope: DeleteScope) => {
            setHeld(null);
            void deleteMessage(held.id, scope);
          }}
          onReact={(slug) => void setReaction(held.id, slug)}
        />
      )}

      {viewerImage && <ImageViewer image={viewerImage} onClose={() => navigate(-1)} />}
    </div>
  );
}
